import { computed, observable, reaction } from "mobx";
import {
  type AbstractModelClass,
  type AnyModel,
  getParent,
  getRootStore,
  getSnapshot,
  idProp,
  isoStringToDateTransform,
  Model,
  tProp,
  types,
} from "mobx-keystone";
import { getStoreConfig } from "../../../config";
import { getEnv } from "../../../env";
import { type CoreError, KeystoneError } from "../error";
import { TStore } from "./t-store";
import type RootStore from "../../root";

type AnyTStore<M extends TModel> = TStore<M, any, any>;

export abstract class TModel extends Model({
  id: idProp,
  createdAt: tProp(types.string).withTransform(isoStringToDateTransform()),
  updatedAt: tProp(types.string).withTransform(isoStringToDateTransform()),
}) {
  creationError = false;
  detaching = false;
  @observable
  isLocked = false;

  getRefId(): string {
    return this.id;
  }

  @observable
  loading = false;

  @observable
  syncing = false;

  @observable
  errors: CoreError[] = [];

  @computed
  get root() {
    if (this.detaching) {
      // biome-ignore lint/nursery/noUselessUndefined: <required for getter>
      return undefined;
    }

    const root = getRootStore<RootStore>(this);

    return root;
  }

  @computed
  private get store(): AnyTStore<this> | AnyModel {
    if (this.detaching) {
      // return null while detaching, since removing from _data triggers reactions;
      // this ensures reactions don’t run against a model that’s being deleted
      return null as unknown as AnyTStore<this> | AnyModel;
    }

    const firstParent: AnyTStore<this> | AnyModel | unknown[] | undefined =
      getParent(this);

    if (!firstParent) {
      throw new KeystoneError(
        "Parent model not found... are you sure this model is attached to a store?",
      );
    } else if (Array.isArray(firstParent)) {
      const storeParent = getParent(firstParent);
      if (!storeParent) {
        throw new KeystoneError(
          "Parent store not found... are you sure this model is attached to a store?",
        );
      }
      return storeParent;
    }

    return firstParent;
  }

  @computed
  get isInStore(): boolean {
    try {
      if (
        this.store &&
        "$modelType" in this.store &&
        typeof this.store.$modelType === "string"
      ) {
        return this.store.$modelType.endsWith("Store");
      }
    } catch (e) {
      console.error(e);
    }
    return false;
  }

  @computed
  get isSelected(): boolean {
    if (this.isInStore && this.store instanceof TStore) {
      return !!this.store._selected.find((item) => item.id === this.id);
    }
    return false;
  }

  @computed
  get selectionIndex(): number {
    if (this.isInStore && this.store instanceof TStore) {
      return this.store._selected.findIndex((item) => item.id === this.id);
    }
    return -1;
  }

  @computed
  get isHidden(): boolean {
    if (this.isInStore && this.store instanceof TStore) {
      return !!this.store._hidden.find((item) => item.id === this.id);
    }
    return false;
  }

  @computed
  get hiddenIndex(): number {
    if (this.isInStore && this.store instanceof TStore) {
      return this.store._hidden.findIndex((item) => item.id === this.id);
    }
    return -1;
  }

  protected onAttachedToRootStore(_root: RootStore) {
    if (getEnv().persistLocally) {
      let callbackId: number | null = null;

      const disposer = reaction(
        () => getSnapshot(this),
        (snap) => {
          void getStoreConfig()
            .storageAdapter.set(this.$modelType, this.id, snap)
            .finally(() => {
              callbackId = null;
            });
        },
        {
          fireImmediately: true,
          name: `${this.$modelType}:[${this.$modelId}]-disposer`,
          scheduler: (run) => {
            if (callbackId) {
              cancelIdleCallback(callbackId);
            }
            callbackId = requestIdleCallback(run);
          },
        },
      );

      return disposer;
    }
  }

  protected override onInit(): void {
    const typeCheckError = this.typeCheck();

    if (typeCheckError) {
      this.creationError = true;
      this.errors.push({
        message: `Invalid ${this.$modelType} snapshot: ${typeCheckError.message}`,
      } as CoreError);
    }
  }

  // @modelFlow
  // public update = _async(function* (this: TModel, data: Self<typeof this>) {
  //   if (this.isInStore && this.store instanceof TStore) {
  //     return yield* _await(this.store.updateById(this.id, data));
  //   }
  // });

  // @modelFlow
  // public sync = _async(function* (this: TModel) {
  //   if (this.isInStore && this.store instanceof TStore) {
  //     return yield* _await(this.store.syncById(this.id));
  //   }
  // });
}

export type TXModel = AbstractModelClass<TModel>;
export type TXModelInstance = InstanceType<TXModel>;
